import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
} from "@tfg/types/constants/communication-studio.constant"
import { BaseClass } from "@tfg/types/schemas/communication-studio/classes"
import { CSSVariableType } from "@tfg/types/types/communication-studio/css-variables.types"
import {
  OrganizationTitleCategoryStylingParameters,
  ProductAttributesCategoryStylingParameters,
  ProductImageCategoryStylingParameters,
  StylingParameters,
} from "@tfg/types/types/communication-studio/styling-parameters.types"

export const useTranslationsMapper = () => {
  const { t } = useTranslation("communicationStudio")
  const globalStylingParametersTranslation = useMemo(
    () =>
      ({
        height: t("tabs.design.style.height"),
        width: t("tabs.design.style.width"),
        fontFamily: t("tabs.design.style.fontFamily"),
        headerFontWeight: {
          value: t("tabs.design.style.headerFontWeight"),
        },
        letterSpacing: t("tabs.design.style.letterSpacing"),
        lineHeight: t("tabs.design.style.lineHeight"),
        backgroundColor: t("tabs.design.style.backgroundColor"),
        contentVerticalSpacing: {
          value: t("tabs.design.style.contentVerticalSpacing"),
        },
        appHeader: {
          props: {
            backgroundColor: t("tabs.design.style.appHeader.backgroundColor"),
            marginBottom: t("tabs.design.style.appHeader.marginBottom"),
          },
        } as Pick<BaseClass, "props">,
        mainContainer: {
          props: {
            letterSpacing: t(
              "tabs.design.style.mainContainer.props.letterSpacing"
            ),
            textAlign: t("tabs.design.style.mainContainer.props.textAlign"),
            textTransform: t(
              "tabs.design.style.mainContainer.props.textTransform"
            ),
            justifyContent: t(
              "tabs.design.style.mainContainer.props.justifyContent"
            ),
          },
        } as Pick<BaseClass, "props">,
        paddingInline: {
          value: t("tabs.design.style.paddingInline.value"),
        },
        paddingBlock: {
          value: t("tabs.design.style.paddingBlock"),
        },
        titleColor: {
          value: t("tabs.design.style.titleColor.value"),
        },
        textColor: {
          value: t("tabs.design.style.textColor.value"),
        },
        textFontSize: {
          value: t("tabs.design.style.textFontSize"),
        },
        headerFontSize: {
          value: t("tabs.design.style.headerFontSize"),
        },
        textFontWeight: {
          value: t("tabs.design.style.textFontWeight.value"),
        },
        headerTextDecoration: {
          value: t("tabs.design.style.headerTextDecoration"),
        },
        headerTextTransform: {
          value: t("tabs.design.style.headerTextTransform"),
        },
        headerFontFamily: {
          value: t("tabs.design.style.headerFontFamily"),
        },
        headerLineHeight: {
          value: t("tabs.design.style.headerLineHeight"),
        },
        contentBlocks: {
          props: {
            gap: t("tabs.design.style.contentBlocks.props.gap"),
          },
        } as Pick<BaseClass, "props">,
      }) satisfies Record<
        keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.GLOBAL],
        string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
      >,
    [t]
  )

  const activitiesStylingParametersTranslation = useMemo(
    () => ({
      activity: {
        props: {
          fontSize: t("tabs.design.style.activities.activity.props.fontSize"),
          fontWeight: t(
            "tabs.design.style.activities.activity.props.fontWeight"
          ),
          textTransform: t(
            "tabs.design.style.activities.activity.props.textTransform"
          ),
          lineHeight: t(
            "tabs.design.style.activities.activity.props.lineHeight"
          ),
          letterSpacing: t(
            "tabs.design.style.activities.activity.props.letterSpacing"
          ),
        },
      },
      country: {
        props: {
          fontSize: t("tabs.design.style.activities.country.props.fontSize"),
          fontWeight: t(
            "tabs.design.style.activities.country.props.fontWeight"
          ),
          textTransform: t(
            "tabs.design.style.activities.country.props.textTransform"
          ),
          letterSpacing: t(
            "tabs.design.style.activities.country.props.letterSpacing"
          ),
        },
      },
      showActivityIcons: t("tabs.design.style.activities.showActivityIcons"),
      showCountryFlags: t("tabs.design.style.activities.showCountryFlags"),
      showCertificates: t("tabs.design.style.activities.showCertificates"),
      activitiesDepth: t("tabs.design.style.activities.activitiesDepth"),
    }),
    [t]
  ) satisfies Record<
    keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.ACTIVITIES],
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const packagingInfoStylingParametersTranslation = useMemo(
    () => ({
      packagingInfoHyperLink: {
        props: {
          fontWeight: t(
            "tabs.design.style.packagingInfo.packagingInfoHyperLink.fontWeight"
          ),
          color: t(
            "tabs.design.style.packagingInfo.packagingInfoHyperLink.color"
          ),
          textDecoration: t(
            "tabs.design.style.packagingInfo.packagingInfoHyperLink.textDecoration"
          ),
        },
      },
      packagingInfoHeader: {
        props: {
          fontWeight: t(
            "tabs.design.style.packagingInfo.packagingInfoHeader.fontWeight"
          ),
          textTransform: t(
            "tabs.design.style.packagingInfo.packagingInfoHeader.textTransform"
          ),
        },
      },
      marginBottom: t("tabs.design.style.packagingInfo.marginBottom"),
      recycledPackagingKeyInfo: {
        props: {
          fontWeight: t(
            "tabs.design.style.packagingInfo.recycledPackagingKeyInfo.fontWeight"
          ),
        },
      },
    }),
    [t]
  ) satisfies Record<
    keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.PACKAGING_INFO],
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const searchEngineStylingParametersTranslation = useMemo(
    () => ({
      backgroundColor: t("tabs.design.style.searchEngine.backgroundColor"),
      searchEngineTextColor: {
        value: t("tabs.design.style.searchEngine.searchEngineTextColor.value"),
      },
      fontFamily: t("tabs.design.style.searchEngine.fontFamily"),
      productCardBadgeBackgroundColor: {
        value: t(
          "tabs.design.style.searchEngine.productCardBadgeBackgroundColor.value"
        ),
      },
      productCardBadgeTextColor: {
        value: t(
          "tabs.design.style.searchEngine.productCardBadgeTextColor.value"
        ),
      },
      searchEngineHeader: {
        props: {
          color: t("tabs.design.style.searchEngine.searchEngineHeader.color"),
          backgroundColor: t(
            "tabs.design.style.searchEngine.searchEngineHeader.backgroundColor"
          ),
        },
      },
    }),
    [t]
  ) satisfies Record<
    keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.SEARCH_ENGINE],
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const footerStylingParametersTranslation = useMemo(
    () => ({
      marginBottom: t("tabs.design.style.footer.marginBottom"),
      marginTop: t("tabs.design.style.footer.marginTop"),
      lastUpdate: {
        props: {
          fontSize: t("tabs.design.style.footer.lastUpdate.fontSize"),
          fontWeight: t("tabs.design.style.footer.lastUpdate.fontWeight"),
        },
      },
      showLastUpdateTimestamp: t(
        "tabs.design.style.footer.showLastUpdateTimestamp"
      ),
      showPoweredByTFG: t("tabs.design.style.footer.showPoweredByTFG"),
    }),
    [t]
  ) satisfies Record<
    keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.FOOTER],
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const ecoscoreStylingParametersTranslation = useMemo(
    () => ({
      ecoscoreAlign: {
        value: t("tabs.design.style.ecoscore.ecoscoreAlign.value"),
      },
      [CommunicationStudioClasses.ECOSCORE_B2B]: {
        props: {
          textAlign: t("tabs.design.style.ecoscore.ecoscoreB2b.textAlign"),
          fontSize: t("tabs.design.style.ecoscore.ecoscoreB2b.fontSize"),
          fontWeight: t("tabs.design.style.ecoscore.ecoscoreB2b.fontWeight"),
          textTransform: t(
            "tabs.design.style.ecoscore.ecoscoreB2b.textTransform"
          ),
        },
      },
    }),
    [t]
  ) satisfies Record<
    keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.ECOSCORE],
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const logoStylingParametersTranslation = useMemo(
    () => ({
      logoAlign: {
        value: t("tabs.design.style.logo.logoAlign"),
      },
      logoFile: t("tabs.design.style.logo.logoFile"),
      logo: {
        props: {
          height: t("tabs.design.style.logo.height"),
          width: t("tabs.design.style.logo.width"),
        },
      },
    }),
    [t]
  ) satisfies Record<
    keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.LOGO],
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const genericPassportTitleStylingParametersTranslation = useMemo<
    Record<
      keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE],
      string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
    >
  >(
    () => ({
      genericPassportTitle: {
        props: {
          color: t("tabs.design.style.genericPassportTitle.color"),
          backgroundColor: t(
            "tabs.design.style.genericPassportTitle.background"
          ),
          fontSize: t("tabs.design.style.genericPassportTitle.fontSize"),
          textTransform: t(
            "tabs.design.style.genericPassportTitle.textTransform"
          ),
          fontWeight: t("tabs.design.style.genericPassportTitle.fontWeight"),
          textDecoration: t(
            "tabs.design.style.genericPassportTitle.textDecoration"
          ),
          lineHeight: t("tabs.design.style.genericPassportTitle.lineHeight"),
          fontFamily: t("tabs.design.style.genericPassportTitle.fontFamily"),
          letterSpacing: t(
            "tabs.design.style.genericPassportTitle.letterSpacing"
          ),
          textAlign: t("tabs.design.style.genericPassportTitle.textAlign"),
        },
      },
    }),
    [t]
  )

  const detailedProductCompositionTitleStylingParametersTranslation = useMemo(
    () => ({
      [CommunicationStudioClasses.DETAILED_PRODUCT_COMPOSITION_TITLE]: {
        props: {
          fontSize: t(
            "tabs.design.style.detailedProductComposition.title.fontSize"
          ),
          fontWeight: t(
            "tabs.design.style.detailedProductComposition.title.fontWeight"
          ),
          textTransform: t(
            "tabs.design.style.detailedProductComposition.title.textTransform"
          ),
          fontFamily: t(
            "tabs.design.style.detailedProductComposition.title.fontFamily"
          ),
          letterSpacing: t(
            "tabs.design.style.detailedProductComposition.title.letterSpacing"
          ),
          paddingBottom: t(
            "tabs.design.style.detailedProductComposition.title.paddingBottom"
          ),
          lineHeight: t(
            "tabs.design.style.detailedProductComposition.title.lineHeight"
          ),
          textDecoration: t(
            "tabs.design.style.detailedProductComposition.title.textDecoration"
          ),
          textAlign: t(
            "tabs.design.style.detailedProductComposition.title.textAlign"
          ),
          color: t("tabs.design.style.detailedProductComposition.title.color"),
        },
      },
      [CommunicationStudioClasses.DETAILED_PRODUCT_COMPOSITION_CONTENT]: {
        props: {
          fontSize: t(
            "tabs.design.style.detailedProductComposition.content.fontSize"
          ),
          fontWeight: t(
            "tabs.design.style.detailedProductComposition.content.fontWeight"
          ),
          textTransform: t(
            "tabs.design.style.detailedProductComposition.content.textTransform"
          ),
          color: t(
            "tabs.design.style.detailedProductComposition.content.color"
          ),
          textAlign: t(
            "tabs.design.style.detailedProductComposition.content.textAlign"
          ),
          fontFamily: t(
            "tabs.design.style.detailedProductComposition.content.fontFamily"
          ),
          paddingBottom: t(
            "tabs.design.style.detailedProductComposition.content.paddingBottom"
          ),
        },
      },
    }),
    [t]
  ) satisfies Record<
    keyof StylingParameters[typeof CUSTOMISATION_CATEGORIES.DETAILED_PRODUCT_COMPOSITION],
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >
  const productAttributesStylingParametersTranslation = useMemo(
    () => ({
      [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION]: {
        props: {
          fontSize: t(
            "tabs.design.style.productAttributes.primaryInformation.fontSize"
          ),
          fontWeight: t(
            "tabs.design.style.productAttributes.primaryInformation.fontWeight"
          ),
          textTransform: t(
            "tabs.design.style.productAttributes.primaryInformation.textTransform"
          ),
          color: t(
            "tabs.design.style.productAttributes.primaryInformation.color"
          ),
          textAlign: t(
            "tabs.design.style.productAttributes.primaryInformation.textAlign"
          ),
          fontFamily: t(
            "tabs.design.style.productAttributes.primaryInformation.fontFamily"
          ),
        },
      },
      [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION]: {
        props: {
          fontSize: t(
            "tabs.design.style.productAttributes.secondaryInformation.fontSize"
          ),
          fontWeight: t(
            "tabs.design.style.productAttributes.secondaryInformation.fontWeight"
          ),
          textTransform: t(
            "tabs.design.style.productAttributes.secondaryInformation.textTransform"
          ),
          color: t(
            "tabs.design.style.productAttributes.secondaryInformation.color"
          ),
          textAlign: t(
            "tabs.design.style.productAttributes.secondaryInformation.textAlign"
          ),
          fontFamily: t(
            "tabs.design.style.productAttributes.secondaryInformation.fontFamily"
          ),
        },
      },
      [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_CONTAINER]: {
        props: {
          gap: t(
            "tabs.design.style.productAttributes.productAttributesContainer.gap"
          ),
          marginBottom: t(
            "tabs.design.style.productAttributes.productAttributesContainer.marginBottom"
          ),
          alignItems: t(
            "tabs.design.style.productAttributes.productAttributesContainer.alignItems"
          ),
        },
      },
    }),
    [t]
  ) satisfies Record<
    keyof ProductAttributesCategoryStylingParameters,
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const organizationTitleStylingParametersTranslation = useMemo(
    () => ({
      organizationTitle: {
        props: {
          fontSize: t("tabs.design.style.organizationTitle.fontSize"),
          fontWeight: t("tabs.design.style.organizationTitle.fontWeight"),
          textTransform: t("tabs.design.style.organizationTitle.textTransform"),
          color: t("tabs.design.style.organizationTitle.color"),
          textAlign: t("tabs.design.style.organizationTitle.textAlign"),
          backgroundColor: t(
            "tabs.design.style.organizationTitle.backgroundColor"
          ),
          fontFamily: t("tabs.design.style.organizationTitle.fontFamily"),
        },
      },
    }),
    [t]
  ) satisfies Record<
    keyof OrganizationTitleCategoryStylingParameters,
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const productImageStylingParametersTranslation = useMemo(
    () => ({
      productImage: {
        props: {
          width: t("tabs.design.style.productImage.width"),
          height: t("tabs.design.style.productImage.height"),
        },
      },
      imageAlign: {
        value: t("tabs.design.style.productImage.imageAlign"),
      },
    }),
    [t]
  ) satisfies Record<
    keyof ProductImageCategoryStylingParameters,
    string | Pick<BaseClass, "props"> | Pick<CSSVariableType, "value">
  >

  const categoriesTranslations = useMemo(
    () => ({
      global: t("tabs.design.categories.global"),
      mainHeader: t("tabs.design.categories.mainHeader"),
      activities: t("tabs.design.categories.activities"),
      packagingInfo: t("tabs.design.categories.packagingInfo"),
      searchEngine: t("tabs.design.categories.searchEngine"),
      footer: t("tabs.design.categories.footer"),
      ecoscore: t("tabs.design.categories.ecoscore"),
      logo: t("tabs.design.categories.logo"),
      productAttributes: t("tabs.design.categories.productAttributes"),
      organizationTitle: t("tabs.design.categories.organizationTitle"),
      productImage: t("tabs.design.categories.productImage"),
      genericPassportTitle: t("tabs.design.categories.genericPassportTitle"),
      detailedProductComposition: t(
        "tabs.design.categories.detailedProductComposition"
      ),
    }),
    [t]
  )

  const translationsMapper = useMemo(
    () => ({
      global: globalStylingParametersTranslation,
      activities: activitiesStylingParametersTranslation,
      packagingInfo: packagingInfoStylingParametersTranslation,
      searchEngine: searchEngineStylingParametersTranslation,
      footer: footerStylingParametersTranslation,
      ecoscore: ecoscoreStylingParametersTranslation,
      logo: logoStylingParametersTranslation,
      productAttributes: productAttributesStylingParametersTranslation,
      organizationTitle: organizationTitleStylingParametersTranslation,
      productImage: productImageStylingParametersTranslation,
      genericPassportTitle: genericPassportTitleStylingParametersTranslation,
      detailedProductComposition:
        detailedProductCompositionTitleStylingParametersTranslation,
    }),
    [
      globalStylingParametersTranslation,
      activitiesStylingParametersTranslation,
      packagingInfoStylingParametersTranslation,
      searchEngineStylingParametersTranslation,
      footerStylingParametersTranslation,
      ecoscoreStylingParametersTranslation,
      logoStylingParametersTranslation,
      productAttributesStylingParametersTranslation,
      organizationTitleStylingParametersTranslation,
      productImageStylingParametersTranslation,
      genericPassportTitleStylingParametersTranslation,
      detailedProductCompositionTitleStylingParametersTranslation,
    ]
  )

  return {
    categoriesTranslations,
    translationsMapper,
  }
}
